import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Video,
  UserRound,
  Building2,
  Layers,
  BookText,
  Atom,
  FlaskConical,
  GraduationCap,
  Archive,
  Settings,
  Blocks,
  Clapperboard,
  PackageOpen,
} from "lucide-react";
import { useThemeLogos } from "@/hooks/useThemeLogos";
import type { LucideIcon } from "lucide-react";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";
import { OnboardingChecklist } from "@/components/onboarding/OnboardingChecklist";
import { useSideRail } from "@/contexts/SideRailContext";
import { useEffect } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useQuery } from "@tanstack/react-query";
import { FeatureFlags } from "@/constants/constants";
import { useOrganization } from "@/hooks/useOrganization";
import knowledgeGapsService from "@/lib/api/knowledgeGaps";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader,
  SidebarTrigger,
} from "@/components/ui/sidebar";

interface NavItem {
  title: string;
  icon: LucideIcon;
  path: string;
  featureFlag?: FeatureFlags;
  /** Optional sidebar badge — currently used to surface active knowledge gaps. */
  badge?: { count: number; label?: string };
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { title: "Home", icon: LayoutDashboard, path: "/" },
    ],
  },
  {
    label: "Insights",
    items: [
      { title: "Sessions", icon: Video, path: "/analytics/sessions" },
      { title: "People", icon: UserRound, path: "/people" },
      { title: "Accounts", icon: Building2, path: "/accounts" },
      { title: "Performance", icon: Atom, path: "/performance", featureFlag: FeatureFlags.SHOW_PERFORMANCE },
    ],
  },
  {
    label: "Agent",
    items: [
      { title: "Agent Versions", icon: Layers, path: "/agents" },
      { title: "Agent Knowledge", icon: GraduationCap, path: "/agent-knowledge" },
      { title: "Agent Assets", icon: BookText, path: "/content-library" },
      { title: "Source Library", icon: Archive, path: "/source-library" },
      { title: "Product Studio", icon: Clapperboard, path: "/demo-studio" },
      { title: "Context Packs", icon: PackageOpen, path: "/context-packs" },
    ],
  },
  {
    label: "Evaluation",
    items: [
      { title: "Simulations", icon: FlaskConical, path: "/evaluation/simulations" },
      { title: "Evaluators", icon: Atom, path: "/evaluation/evaluators" },
    ],
  },
];

const footerItems: NavItem[] = [
  { title: "Settings", icon: Settings, path: "/organization-settings" },
  { title: "Integrations", icon: Blocks, path: "/integrations" },
];

export function ModernSidebar() {
  const location = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const { toggleSideRail } = useSideRail();
  const { favicon: HobbesIcon } = useThemeLogos();
  const showPerformance = useFeatureFlagEnabled(FeatureFlags.SHOW_PERFORMANCE);
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  // Active unresolved-gap count for the Agent Knowledge sidebar badge. Driven
  // off the same /report endpoint the page uses, so the cached entry is
  // shared and we add no additional request when the user is on the page.
  // Refetched every 60s — frequent enough to feel live, infrequent enough
  // not to spam the backend.
  const { data: kgReport } = useQuery({
    queryKey: ["knowledge-gaps-report", orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await knowledgeGapsService.getReport(orgId);
      return res.success && res.data ? res.data : null;
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const activeGapCount = kgReport?.total_active_summaries ?? 0;

  const flagValues: Record<string, boolean | undefined> = {
    [FeatureFlags.SHOW_PERFORMANCE]: showPerformance,
  };

  const filteredNavGroups = navGroups.map(group => ({
    ...group,
    items: group.items
      .filter(item => !item.featureFlag || flagValues[item.featureFlag])
      .map(item =>
        item.path === "/agent-knowledge" && activeGapCount > 0
          ? { ...item, badge: { count: activeGapCount, label: `${activeGapCount} active gaps` } }
          : item,
      ),
  }));

  // Handle "/" keyboard shortcut to toggle Ask Hobbes side rail
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        toggleSideRail();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSideRail]);

  const isCurrentPath = (path: string) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isFooterActive = (path: string) => {
    if (path === "/organization-settings") {
      return location.pathname.startsWith("/organization-settings") ||
        location.pathname.startsWith("/account-settings");
    }
    return isCurrentPath(path);
  };

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="mt-2 px-3 py-2 flex flex-row items-center justify-between group-data-[collapsible=icon]:!px-2">
        <div className={`flex-1 min-w-0 ${!isMobile ? "group-data-[collapsible=icon]:hidden" : ""}`}>
          <OrganizationSwitcher />
        </div>
        <SidebarTrigger className="ml-2 text-sidebar-foreground hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:ml-0" />
      </SidebarHeader>

      <SidebarContent className="px-2 py-1 group-data-[collapsible=icon]:!px-0">
        {/* Ask Hobbes button */}
        <SidebarGroup className="px-0 py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5 px-1 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:items-center">
              <SidebarMenuButton
                className="mb-1 h-8 w-full justify-start rounded-md border border-sidebar-border/70 bg-sidebar-accent/55 px-2 py-1.5 text-sidebar-accent-foreground shadow-none transition-colors duration-150 hover:bg-sidebar-accent"
                onClick={() => toggleSideRail()}
                tooltip={state === "collapsed" ? "Ask Hobbes" : undefined}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <img src={HobbesIcon} alt="Hobbes" className="w-3.5 h-3.5" />
                  {(isMobile || state === "expanded") && (
                    <span className="truncate text-[13px] font-medium text-sidebar-accent-foreground">Ask Hobbes</span>
                  )}
                </div>
                {(isMobile || state === "expanded") && (
                  <kbd className="ml-auto flex h-4 w-4 items-center justify-center rounded border border-sidebar-border/80 bg-sidebar/60 text-[10px] font-medium text-sidebar-foreground/55">
                    /
                  </kbd>
                )}
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Navigation groups */}
        {filteredNavGroups.map((group, groupIdx) => (
          <SidebarGroup key={group.label ?? groupIdx} className="px-0 py-0">
            {group.label && (
              <SidebarGroupLabel className="px-3 text-[10px] font-medium uppercase tracking-normal text-sidebar-foreground/50 group-data-[collapsible=icon]:hidden mt-4 mb-0.5">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-px px-1 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:items-center">
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isCurrentPath(item.path)}
                      className="w-full h-8 px-2 py-1.5 rounded-md transition-colors duration-150 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                      tooltip={state === "collapsed" ? item.title : undefined}
                    >
                      <Link
                        to={item.path}
                        className="relative w-full flex items-center gap-2 justify-start"
                        aria-label={item.badge && item.badge.count > 0 ? `${item.title}, ${item.badge.label}` : item.title}
                        onClick={handleNavClick}
                      >
                        <item.icon
                          size={15}
                          strokeWidth={isCurrentPath(item.path) ? 2 : 1.5}
                          className={isCurrentPath(item.path)
                            ? "text-sidebar-accent-foreground"
                            : "text-sidebar-foreground"}
                        />
                        {(isMobile || state === "expanded") && (
                          <span className={`text-[13px] ${isCurrentPath(item.path) ? "font-medium text-sidebar-accent-foreground" : "text-sidebar-foreground"}`}>
                            {item.title}
                          </span>
                        )}
                        {(isMobile || state === "expanded") && item.badge && item.badge.count > 0 && (
                          <Badge
                            variant="secondary"
                            className="ml-auto text-[10px] px-1.5 py-0 h-4 leading-none"
                            title={item.badge.label}
                          >
                            {item.badge.count}
                          </Badge>
                        )}
                        {!isMobile && state === "collapsed" && item.badge && item.badge.count > 0 && (
                          <span
                            className="absolute -right-0.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground"
                            title={item.badge.label}
                          >
                            {item.badge.count > 9 ? '9+' : item.badge.count}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="px-2 py-3 group-data-[collapsible=icon]:!px-0 border-t border-sidebar-border">
        <OnboardingChecklist />
        <SidebarGroup className="px-0 py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-px px-1 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:items-center">
              {footerItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isFooterActive(item.path)}
                    className="w-full h-8 px-2 py-1.5 rounded-md transition-colors duration-150 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                    tooltip={state === "collapsed" ? item.title : undefined}
                  >
                    <Link
                      to={item.path}
                      className="w-full flex items-center gap-2 justify-start"
                      onClick={handleNavClick}
                    >
                      <item.icon
                        size={15}
                        strokeWidth={isFooterActive(item.path) ? 2 : 1.5}
                        className={isFooterActive(item.path) ? "text-sidebar-accent-foreground" : "text-sidebar-foreground"}
                      />
                      {(isMobile || state === "expanded") && (
                        <span className={`text-[13px] ${isFooterActive(item.path) ? "font-medium text-sidebar-accent-foreground" : "text-sidebar-foreground"}`}>
                          {item.title}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  );
}
